import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi'
import { z } from 'zod'

extendZodWithOpenApi(z)

// --- Operator category enums ---

const PositiveStringOperatorSchema = z.enum(['contains', 'equals', 'startsWith', 'endsWith', 'is'])
const NegationStringOperatorSchema = z.enum(['doesNotContain', 'doesNotEqual', 'not'])
const StringOperatorSchema = z.enum([...PositiveStringOperatorSchema.options, ...NegationStringOperatorSchema.options])

const PositiveNumericOperatorSchema = z.enum(['>', '>=', '=', '<', '<='])
const NegationNumericOperatorSchema = z.enum(['!='])
const NumericOperatorSchema = z.enum([...PositiveNumericOperatorSchema.options, ...NegationNumericOperatorSchema.options])
export type NumericOperator = z.infer<typeof NumericOperatorSchema>
const EmptyOperatorSchema = z.enum(['isEmpty', 'isNotEmpty'])
const ArrayOperatorSchema = z.enum(['isAnyOf'])

const PositiveDateOperatorSchema = z.enum(['is', 'after', 'onOrAfter', 'before', 'onOrBefore'])
const NegationDateOperatorSchema = z.enum(['not'])
const DateOperatorSchema = z.enum([...PositiveDateOperatorSchema.options, ...NegationDateOperatorSchema.options])

// --- Per-category filter item schemas ---

const idSchema = z.union([z.number(), z.string()]).optional()
const fieldSchema = z.string()

const FieldFilterBase = z.object({
  id: idSchema,
  field: fieldSchema,
  context: z.record(z.string(), z.unknown()).optional(),
})

const PositiveStringFilterBuilderInputSchema = z.object({
  operator: PositiveStringOperatorSchema,
  value: z.string().max(500).optional(),
})

const NegationStringFilterBuilderInputSchema = z.object({
  operator: NegationStringOperatorSchema,
  value: z.string().max(500).optional(),
})

const StringFilterBuilderInputSchema = z.union([PositiveStringFilterBuilderInputSchema, NegationStringFilterBuilderInputSchema])

const PositiveNumericFilterBuilderInputSchema = z.object({
  operator: PositiveNumericOperatorSchema,
  value: z.number().optional(),
})

const NegationNumericFilterBuilderInputSchema = z.object({
  operator: NegationNumericOperatorSchema,
  value: z.number().optional(),
})

const NumericFilterBuilderInputSchema = z.union([
  PositiveNumericFilterBuilderInputSchema,
  NegationNumericFilterBuilderInputSchema,
])

const IsEmptyFilterBuilderInputSchema = z.object({
  operator: z.literal('isEmpty'),
})

const IsNotEmptyFilterBuilderInputSchema = z.object({
  operator: z.literal('isNotEmpty'),
})

const ArrayFilterBuilderInputSchema = z.object({
  operator: ArrayOperatorSchema,
  value: z.array(z.string()).optional(),
})

const PositiveDateFilterBuilderInputSchema = z.object({
  operator: PositiveDateOperatorSchema,
  value: z.iso.date().optional(),
})

const NegationDateFilterBuilderInputSchema = z.object({
  operator: NegationDateOperatorSchema,
  value: z.iso.date().optional(),
})

const DateFilterBuilderInputSchema = z.union([PositiveDateFilterBuilderInputSchema, NegationDateFilterBuilderInputSchema])

// --- Combined schemas ---

const FilterBuilderInputWithValueSchema = z.union([
  StringFilterBuilderInputSchema,
  NumericFilterBuilderInputSchema,
  ArrayFilterBuilderInputSchema,
  DateFilterBuilderInputSchema,
])
const FilterBuilderInputWithoutValueSchema = z.union([IsEmptyFilterBuilderInputSchema, IsNotEmptyFilterBuilderInputSchema])
const FilterBuilderInputSchema = z.union([FilterBuilderInputWithValueSchema, FilterBuilderInputWithoutValueSchema])
export type FilterBuilderInput = z.infer<typeof FilterBuilderInputSchema>
// --- Positive (non-negation) filter builder input ---

const PositiveFilterBuilderInputSchema = z.union([
  PositiveStringFilterBuilderInputSchema,
  PositiveNumericFilterBuilderInputSchema,
  ArrayFilterBuilderInputSchema,
  IsNotEmptyFilterBuilderInputSchema,
  PositiveDateFilterBuilderInputSchema,
])
export type PositiveFilterBuilderInput = z.infer<typeof PositiveFilterBuilderInputSchema>

// --- Grid filter item schemas ---

const StringFilterItemSchema = z.union([
  z.object({ ...FieldFilterBase.shape, ...PositiveStringFilterBuilderInputSchema.shape }),
  z.object({ ...FieldFilterBase.shape, ...NegationStringFilterBuilderInputSchema.shape }),
])
const NumericFilterItemSchema = z.union([
  z.object({ ...FieldFilterBase.shape, ...PositiveNumericFilterBuilderInputSchema.shape }),
  z.object({ ...FieldFilterBase.shape, ...NegationNumericFilterBuilderInputSchema.shape }),
])
const IsEmptyFilterItemSchema = z.object({ ...FieldFilterBase.shape, ...IsEmptyFilterBuilderInputSchema.shape })
const IsNotEmptyFilterItemSchema = z.object({ ...FieldFilterBase.shape, ...IsNotEmptyFilterBuilderInputSchema.shape })
const EmptyFilterItemSchema = z.union([IsEmptyFilterItemSchema, IsNotEmptyFilterItemSchema])
const ArrayFilterItemSchema = z.object({ ...FieldFilterBase.shape, ...ArrayFilterBuilderInputSchema.shape })
const DateFilterItemSchema = z.union([
  z.object({ ...FieldFilterBase.shape, ...PositiveDateFilterBuilderInputSchema.shape }),
  z.object({ ...FieldFilterBase.shape, ...NegationDateFilterBuilderInputSchema.shape }),
])

const GridFilterItemWithValueSchema = z.union([
  StringFilterItemSchema,
  NumericFilterItemSchema,
  ArrayFilterItemSchema,
  DateFilterItemSchema,
])
const GridFilterItemWithoutValueSchema = z.union([IsEmptyFilterItemSchema, IsNotEmptyFilterItemSchema])
const GridFilterItemSchema = z.union([GridFilterItemWithValueSchema, GridFilterItemWithoutValueSchema])

export type GridFilterItemWithValue = z.infer<typeof GridFilterItemWithValueSchema>
export type GridFilterItem = z.infer<typeof GridFilterItemSchema>

export const isGridFilterItemWithValue = (item: GridFilterItem): item is GridFilterItemWithValue => {
  return item.operator !== 'isEmpty' && item.operator !== 'isNotEmpty'
}

export const isStringValue = (value: SupportedFilterItemValue): value is string => typeof value === 'string'

export const isArrayValue = (value: SupportedFilterItemValue): value is string[] => Array.isArray(value)
export const itemValueIsEmpty = (item: GridFilterItemWithValue) => {
  if (item.value == null) {
    return true
  }

  if (Array.isArray(item.value)) {
    return item.value.length === 0
  }

  if (typeof item.value === 'string') {
    return item.value.trim() === ''
  }

  return false
}

// --- Flat schemas kept for independent operator/value validation ---

export const SupportedFilterItemOperatorSchema = z.enum([
  ...StringOperatorSchema.options,
  ...NumericOperatorSchema.options,
  ...EmptyOperatorSchema.options,
  ...ArrayOperatorSchema.options,
  ...DateOperatorSchema.options,
])
export type SupportedFilterItemOperator = z.infer<typeof SupportedFilterItemOperatorSchema>

export const SupportedFilterItemValueSchema = z.union([z.string(), z.array(z.string()), z.number()]).optional()
export type SupportedFilterItemValue = z.infer<typeof SupportedFilterItemValueSchema>

// --- Sub-schemas and sub-types for consumers that want to narrow ---
export {
  GridFilterItemSchema,
  StringFilterItemSchema,
  NumericFilterItemSchema,
  EmptyFilterItemSchema,
  ArrayFilterItemSchema,
  DateFilterItemSchema,
  StringOperatorSchema,
  NumericOperatorSchema,
  EmptyOperatorSchema,
  ArrayOperatorSchema,
  DateOperatorSchema,
}

// --- Negation filter builder input ---

export const NegationOperatorSchema = z.enum([
  ...NegationStringOperatorSchema.options,
  ...NegationNumericOperatorSchema.options,
  ...NegationDateOperatorSchema.options,
  'isEmpty',
])
export type NegationOperator = z.infer<typeof NegationOperatorSchema>

export const isNegationOperator = (operator: string): operator is NegationOperator =>
  NegationOperatorSchema.options.includes(operator as NegationOperator)

const NegationFilterBuilderInputSchema = z.union([
  NegationStringFilterBuilderInputSchema,
  NegationNumericFilterBuilderInputSchema,
  NegationDateFilterBuilderInputSchema,
  IsEmptyFilterBuilderInputSchema,
])
export type NegationFilterBuilderInput = z.infer<typeof NegationFilterBuilderInputSchema>

export const isNegationFilterBuilderInput = (input: FilterBuilderInput): input is NegationFilterBuilderInput => {
  return isNegationOperator(input.operator)
}

// --- Grid logic operator ---
export const GridLogicOperator = {
  And: 'and',
  Or: 'or',
} as const

export type GridLogicOperatorValue = (typeof GridLogicOperator)[keyof typeof GridLogicOperator]

export const GridLogicOperatorSchema = z.enum(['and', 'or'])

// --- Grid filter/sort models ---

export const GridFilterModelSchema = z
  .object({
    items: z.array(GridFilterItemSchema),
    logicOperator: GridLogicOperatorSchema.optional(),
    quickFilterValues: z.array(z.string().max(500)).optional(),
  })
  .openapi('GridFilterModel')
export type GridFilterModel = z.infer<typeof GridFilterModelSchema>

/**
 * Splits a search box's free text into quick-filter terms. Each term is matched independently and
 * the terms are ANDed, so one term per word is what makes a multi-word search work — passing the
 * whole phrase only matches text containing it verbatim.
 */
export const toQuickFilterValues = (query: string): string[] => query.trim().split(/\s+/).filter(Boolean)

export const GridSortModelSchema = z
  .array(
    z.object({
      field: z.string(),
      sort: z.enum(['asc', 'desc']),
    })
  )
  .openapi('GridSortModel')
export type GridSortModel = z.infer<typeof GridSortModelSchema>
